import { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

export default function PredictiveStrategyRecalibration() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  // "Send to Strategy Module": per-card in-flight flag and error message.
  const [sending, setSending] = useState<Record<number, boolean>>({});
  const [sendErrors, setSendErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
          setError('User ID not found');
          setLoading(false);
          return;
        }

        const res = await apiService.getPredictiveRecalibration(userId);
        // Sample recommendations (is_fallback, from an older backend) are never shown:
        // they could be sent to the Strategy module as if they were real.
        if (res?.is_fallback ?? res?.data?.is_fallback) {
          setError('Live analysis is unavailable right now. Please try again.');
        } else if (res.status === 'success' && res.data) {
          setData(res.data);
        } else {
          setError(res?.message || res?.data?.message || 'Failed to load predictive recalibration data');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reloadKey]);

  // Re-ask quietly (no spinner) and swap the answer in; refresh=true is the
  // Refresh button (at most once a day, server-side). data.updated_at /
  // refreshing / preparing come with the report.
  const refetchQuietly = async (refresh = false) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    const res = await apiService.getPredictiveRecalibration(userId, refresh);
    if (res?.status === 'success' && res.data && !(res.is_fallback ?? res.data.is_fallback)) setData(res.data);
  };
  const freshness = useBackgroundRefresh(data?.refreshing, () => refetchQuietly());
  const preparing = useBackgroundRefresh(data?.preparing, () => refetchQuietly(), PREPARING_POLL);

  const handleSend = async (idx: number) => {
    if (!data || data.empty || !data.recommendations) return;
    
    const rec = data.recommendations[idx];
    const userId = localStorage.getItem('user_id');

    if (!userId) {
      setSendErrors(prev => ({ ...prev, [idx]: "We couldn't identify your account. Please sign in again." }));
      return;
    }

    setSending(prev => ({ ...prev, [idx]: true }));
    setSendErrors(prev => ({ ...prev, [idx]: '' }));
    try {
      const res = await apiService.sendToStrategy({
        user_id: userId,
        title: rec.title,
        rationale: rec.rationale,
        impact_level: rec.impact
      });
      // A 200 can still carry {data: {success: false, message}}: that is a failure.
      if (res?.data?.success === false) {
        throw new Error(res.data.message || "The recommendation wasn't saved.");
      }

      // Mark as sent only once the backend has confirmed it.
      setData((prev: any) => ({
        ...prev,
        recommendations: prev.recommendations.map((r: any, i: number) =>
          i === idx ? { ...r, sent: true } : r
        )
      }));
    } catch (err: any) {
      console.error('Failed to send to strategy:', err);
      setSendErrors(prev => ({
        ...prev,
        [idx]: `Couldn't send to Strategy Module. ${err?.message || 'Please try again.'}`
      }));
    } finally {
      setSending(prev => ({ ...prev, [idx]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
        <div className="text-sm font-medium text-gray-600">Generating AI Predictive Intelligence...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-3xl text-red-500 mb-2"></i>
        <h3 className="text-red-800 font-medium">Failed to load Dashboard</h3>
        <p className="text-sm text-red-600 mt-1">{error}</p>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-3 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-xs font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Nothing to analyse yet (e.g. no business profile): no recommendations, nothing to send.
  if (data.preparing) {
    return <PreparingNotice state={preparing} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  if (data.empty) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
        <i className="ri-information-line text-3xl text-gray-400 mb-2"></i>
        <p className="text-sm text-gray-600">{data.message || 'Nothing to show yet.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DataFreshnessBanner updatedAt={data.updated_at} state={freshness} onRefresh={() => refetchQuietly(true)} />

      {/* Decision Intelligence Engine */}
      <div className="grid grid-cols-1 gap-6">

        {/* AI Recommendation Feed */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <i className="ri-robot-2-line text-violet-600 text-lg"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">AI Recommendation Feed</h3>
              <p className="text-xs text-gray-500">Actionable strategy shifts derived from predictive models</p>
            </div>
          </div>
          <div className="space-y-3">
            {data.recommendations?.map((rec: any, i: number) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        rec.impact === 'Critical' ? 'bg-red-100 text-red-700' :
                        rec.impact === 'High' ? 'bg-amber-100 text-amber-700' :
                        'bg-sky-100 text-sky-700'
                      }`}>
                        {rec.impact}
                      </span>
                      {rec.sent && (
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <i className="ri-check-line mr-1"></i>
                          {rec.used ? 'Used in your content calendar' : 'Sent — shapes your next content calendar'}
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{rec.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{rec.rationale}</p>
                  </div>
                </div>
                {!rec.sent && (
                  <button
                    onClick={() => handleSend(i)}
                    disabled={sending[i]}
                    className="mt-3 px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending[i] ? 'Sending…' : 'Send to Strategy Module'}
                  </button>
                )}
                {sendErrors[i] && (
                  <p className="mt-2 text-xs text-red-600 flex items-start gap-1">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{sendErrors[i]}</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Predictive Recommendation Dashboard */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <i className="ri-brain-line text-violet-600 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Predictive Recommendation Dashboard</h3>
            <p className="text-xs text-gray-500">AI-generated future strategy recommendations, format optimization, platform adjustments, theme recalibration, and opportunity insights</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {data.predictive_recommendations?.map((rec: any) => (
            <div key={rec.category} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-lg ${rec.iconBg} flex items-center justify-center`}>
                  <i className={`${rec.icon} ${rec.iconColor} text-sm`}></i>
                </div>
                <span className="text-xs font-semibold text-gray-800 leading-tight">{rec.category}</span>
              </div>
              <div className="space-y-2.5">
                {rec.items?.map((item: string, i: number) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-1.5 flex-shrink-0"></div>
                    <p className="text-xs text-gray-600 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Evolution Dashboard */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <i className="ri-radar-line text-violet-600 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Platform Evolution Dashboard</h3>
            <p className="text-xs text-gray-500">Platform updates, algorithm changes, feature launches, policy updates, and adoption opportunities across all active channels</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.platform_evolution?.map((plat: any) => (
            <div key={plat.platform} className={`rounded-xl border p-5 ${plat.color}`}>
              <div className="flex items-center gap-2.5 mb-4">
                <div className={`w-9 h-9 rounded-lg ${plat.iconBg} flex items-center justify-center`}>
                  <i className={`${plat.icon} text-lg`}></i>
                </div>
                <span className="font-semibold">{plat.platform}</span>
              </div>
              <div className="space-y-3.5">
                {plat.updates?.map((u: any, i: number) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-current mt-1.5 flex-shrink-0 opacity-60"></div>
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70 block mb-0.5">{u.label}</span>
                      <p className="text-sm leading-relaxed opacity-90">{u.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optimization Signals */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <i className="ri-signal-tower-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Optimization Signals</h3>
            <p className="text-xs text-gray-500">High-priority actionable signals derived from predictive intelligence and real-time platform data</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.optimization_signals?.map((sig: any, i: number) => (
            <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-5 hover:border-violet-200 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center">
                    <i className={`${sig.icon} text-violet-600 text-sm`}></i>
                  </div>
                  <div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      sig.impact === 'Critical' ? 'bg-red-100 text-red-700' :
                      sig.impact === 'High' ? 'bg-amber-100 text-amber-700' :
                      'bg-sky-100 text-sky-700'
                    }`}>
                      {sig.impact}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 ml-2">{sig.type}</span>
                  </div>
                </div>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2 leading-snug">{sig.signal}</h4>
              <p className="text-xs text-gray-600 leading-relaxed">{sig.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}